import React, { useContext, useEffect, useState } from "react";
import { MODAL_TYPES, ModalType } from "../../constants/ModalTypes";
import { UserContext } from "../../context/UserContext";
import {
    forgotPassword as forgotPasswordAPI,
    login as loginAPI,
    resetPassword as resetPasswordAPI,
    verifyEmail as verifyEmailAPI,
} from "../../services/AuthAPI";
import Login from "../auth/Login";
import Register from "../auth/Register";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import VerifyModal from "./VerifyModal";

interface AuthModalsProps {
    showLogin: boolean;
    setShowLogin: React.Dispatch<React.SetStateAction<boolean>>;
}


const AuthModals: React.FC<AuthModalsProps> = ({ showLogin, setShowLogin }) => {
    const [activeModal, setActiveModal] = useState<ModalType>(null);
    const [code, setCode] = useState<string>("");
    const { setIsLoggedIn } = useContext(UserContext);

    useEffect(() => {
        const handleUrlLogic = async () => {
            // Dynamically check for any modal type in the URL
            const currentPath = window.location.pathname.slice(1); // Remove leading "/"
            const matchedModal = Object.values(MODAL_TYPES).find((modalType) => modalType === currentPath);

            const urlParams = new URLSearchParams(window.location.search);
            const resetCode = urlParams.get("code") || "";
            const userId = urlParams.get("user") || "";

            if (matchedModal) {
                setActiveModal(matchedModal);
            }

            if (resetCode && currentPath === "verify") {
                try {
                    const success = await handleVerifyEmail(resetCode, userId);
                    if (success) {
                        setActiveModal(null);
                        setIsLoggedIn(true);
                        updateURL(null);
                    }
                } catch (error) {
                    console.error("Verification failed: ", error);
                }
            }
        };

        handleUrlLogic();
    }, []);


    useEffect(() => {
        if (showLogin) {
            setActiveModal(MODAL_TYPES.LOGIN);
        }
    }, [showLogin]);

    const updateURL = (modalType: ModalType) => {
        const url = new URL(window.location.href);
        if (modalType) {
            url.pathname = `/${modalType}`;
        } else {
            url.pathname = "/";
        }
        window.history.pushState({}, "", url.toString());
    };

    const openModal = (modalType: ModalType) => {
        setActiveModal(modalType);
        updateURL(modalType); // Update URL when opening a modal
    };

    const closeModals = () => {
        setActiveModal(null);
        setShowLogin(false);
        updateURL(null); // Reset URL when closing modals
    };

    const handleLogin = async (email: string, password: string): Promise<{
        message: string, user: any, success: boolean
    }> => {
        try {
            const data: any = await loginAPI(email, password);
            if (data.message.includes("verify")) {
                openModal(MODAL_TYPES.VERIFY);
            }
            return data;
        } catch (error: any) {
            console.error("Login error: ", error);
            return {
                message: error.message || "Failed to login.",
                user: null,
                success: error.success,
            };
        }
    };

    const handleForgotPassword = async (email: string): Promise<any> => {
        try {
            const data = await forgotPasswordAPI(email);
            if (data.success) {
                setTimeout(() => {
                    openModal(MODAL_TYPES.VERIFY);
                }, 1000);
            }
            return data;
        } catch (error: any) {
            console.error("Forgot Password error: ", error);
            return {
                message: error?.message || "Failed to send password reset link.",
                success: false,
            };
        }
    };

    const handleResetPassword = async (password: string): Promise<any> => {
        try {
            // Get code directly from the URL
            const urlParams = new URLSearchParams(window.location.search);
            const resetCode = urlParams.get("code") || ""; // Use the extracted code directly
            const userId = urlParams.get("user") || ""; // Use the extracted code directly
            if (!resetCode) {
                return {
                    message: "Invalid reset code.",
                    success: false,
                };
            }

            // Use the resetCode for the API call
            const data = await resetPasswordAPI(resetCode, userId, password);
            if (data.success) {
                setTimeout(() => {
                    openModal(MODAL_TYPES.LOGIN);
                }, 1000);
            }
            return data;
        } catch (error: any) {
            console.error("Reset Password error: ", error);
            return {
                message: error?.message || "Failed to reset password.",
                success: false,
            };
        }
    };

    const handleVerifyEmail = async (verificationCode: string, userId: string): Promise<boolean> => {
        try {
            const data: any = await verifyEmailAPI(verificationCode, userId);

            if (data.success) {
                closeModals();
                setCode("");
                return true;
            } else {
                console.error("Verification failed: ", data.message);
            }
        } catch (error) {
            console.error("Verification error: ", error);
        }
        return false;
    };

    return (
        <>
            {activeModal === MODAL_TYPES.LOGIN && (
                <Login
                    onClose={closeModals}
                    onLogin={handleLogin}
                    onRegisterClick={() => openModal(MODAL_TYPES.REGISTER)}
                    onForgotPasswordClick={() => openModal(MODAL_TYPES.FORGOT_PASSWORD)}
                />
            )}
            {activeModal === MODAL_TYPES.REGISTER && (
                <Register
                    onClose={closeModals}
                    onForgotPasswordClick={() => openModal(MODAL_TYPES.FORGOT_PASSWORD)}
                    onLoginClick={() => openModal(MODAL_TYPES.LOGIN)}
                    onResponseData={(response) => {
                        if (response.success) {
                            openModal(MODAL_TYPES.VERIFY);
                        }
                    }}
                />
            )}
            {activeModal === MODAL_TYPES.VERIFY && (
                <VerifyModal onClose={closeModals} onShowLogin={() => openModal(MODAL_TYPES.LOGIN)} />
            )}
            {activeModal === MODAL_TYPES.FORGOT_PASSWORD && (
                <ForgotPassword onClose={closeModals} onSubmit={handleForgotPassword} />
            )}
            {activeModal === MODAL_TYPES.RESET_PASSWORD && (
                <ResetPassword onClose={closeModals} onSubmit={handleResetPassword} />
            )}
        </>
    );
};

export default AuthModals;
